import { nanoid } from "@reduxjs/toolkit";
import { api } from "../../app/api";
import { command } from "../../app/command";
import { query } from "../../app/query";
import filterFalsyParameters from "../../common/helpers/filterFalsyParameters";
import { EventPayload } from "../../common/types/Event";
import { Paginated } from "../../common/types/Paginated";
import {
  OrderRowTemplate,
  SupplementPriceOverridesType,
  SupplementsType,
  VariationSelection,
} from "../../common/types/apiTypes";
import { Options } from "../../common/types/requestOptions";
import {
  CreateOrderRowTemplate,
  UpdateActiveStatus,
  UpdateOrderRowTemplate,
} from "../customer/types/commands";
import { OrderRowTemplateUpdated } from "../customer/types/events";
import { AgreedHours, TargetingRule } from "../employment/employmentApi";
import {
  DateBasedSalary,
  TargetedShiftSalaryRow,
  TargetingKeys,
} from "../employment/targetedShiftSalaries/targetedShiftSalariesApi";
import {
  LanguageSkillCode,
  SupplementPriceOverride,
} from "../talents/talentAPI"; //TODO: Move types to common folder
import { TesSalaryTableSelectionProvenance } from "../tesSalaryTables/tesSalaryTablesApi";
import {
  AutomatchingStatus,
  FulfillmentStatusResponse,
  LanguageSkill,
  LanguageSkillOption,
  OrderPreparedPayload,
  TeamNotificationSettings,
} from "./orderTypes";
import { SalaryType } from "./types/salaryType";
import { TESVariationResponse } from "./types/tesVariationTypes";
import { WorkTimeType } from "./types/workTimeType";

export enum OrderType {
  Short = "ShortOrder",
  FixedTerm = "Fixed-term",
  Continuous = "Continuous",
}

export interface ListAssignedTalent {
  id: string;
  firstName: string;
  lastName: string;
}

export interface BaseOrder {
  customerOrgId: string;
  title: string;
  contractId: string;
  workLocationId: string;
}

// REVIEW what does this need to include
export interface Order extends BaseOrder {
  id: string;
  customerOrgName: string;
  type: OrderType | null;
  orderTime: number;
  rows: OrderRowNew[];
  workStarts: number;
}

export interface OrderRowDisplayTaskSpec {
  certificateIds: string[];
  trainings: string[];
  languageSkills: LanguageSkillOption[];
  skillIds: string[];
  description?: string;
  descriptionForTalent?: string;
}

export interface ShiftDetailDTO {
  custCCId: string;
  start: Date;
  end: Date;
  salaryLimit?: string;
  isFillShift: Boolean;
  flexibleStartUntil?: EpochTimeStamp;
}

export interface OrderRowDisplay {
  id: string;
  orderRowIds: string[];
  bundleId: string;
  title: string;
  customerOrgName: string;
  workLocationName: string;
  workStarts: string; // TODO: should be number / EpochTimeStamp
  workEnds?: string; // TODO: should be number / EpochTimeStamp
  workTitleId: string;
  amountOrdered: number;
  originalAmountOrdered?: number;
  amountFulfilled: number;
  amountCustomerOffered: string;
  status: string;
  // First / "main" handler for the order
  orderHandler: OrderHandler | null;
  orderHandlers: OrderHandler[];
  isFillShift: boolean;
  automatchStatus: string;
  assignedTalents: ListAssignedTalent[];
  manuallyOfferedTalents: OfferedTalent[];
  eezyCompanyId: string;
  disabled: boolean;
  type: OrderType;
  taskSpec: OrderRowDisplayTaskSpec;
  isFlexibleShift: boolean;
  shifts: ShiftDetailDTO[];
  teamNotificationSettings?: TeamNotificationSettings;
}

export interface ShiftDetail {
  custCCId: string;
  time: {
    start: number;
    end: number;
  };
  salaryLimit: string;
  isFillShift: boolean;
  flexibleStartUntil?: EpochTimeStamp;
  isBillable: boolean;
}

export interface AssignedTalent {
  id: string;
  assignmentId: string;
  name: string;
  reasonForCancellation?: string;
  talentGetsPaid: boolean;
  absent?: boolean;
  agreedToStartShiftAt?: EpochTimeStamp;
  isNewWorkerForCustomer?: boolean;
}

export type OfferedTalent = Omit<AssignedTalent, "reasonForCancellation"> & {
  status: string;
};

export interface OrderRowEdit {
  id: string;
  version: number;
  shiftDetail: ShiftDetail;
  bundleId: string;
  editReason?: { editReason: string; editor: string; time: number }[];
  taskSpec: TaskSpec;
  descriptionToEezyAndCustomer: string;
  descriptionToEezyAndCustomerAndTalent: string;
  assignedTalents: AssignedTalent[];
}

export enum FulfillmentStatus {
  None = "None",
  NoneFinished = "NoneFinished",
  Partial = "Partial",
  PartialFinished = "PartialFinished",
  Full = "Full",
  FullFinished = "FullFinished",
}

export interface OrderStatus {
  automatchingStatus: AutomatchingStatus;
  automatchingStartDelayMinutes: number | null;
  fulfillmentStatus: FulfillmentStatus;
  isAutomatchingExhausted: boolean;
  isReadOnly: boolean;
  mainStatus: string;
}

export interface OrderHandler {
  id?: string;
  name: string;
}

// Rename after orderRows is removed
export interface ShiftDetails2 {
  // id is orderRowId for open shifts and assignment id for filled shifts
  id: string;
  orderRowId: string;
  shiftDetail: ShiftDetail;
  assignedTalent?: AssignedTalent;
  version: number;
}

export type OrderEditTargetingRule = {
  custCompany: { id: string; name: string };
  custOrgs: { id: string; name: string }[];
  workLocs: { id: string; name: string }[];
  workTitles: string[];
};

export interface OrderEdit {
  bundleId: string;
  title: string;
  type: OrderType;
  originalAmount?: number;
  assignedTalents?: AssignedTalent[];
  orderQueueId: string;
  contractId: string;
  customerOrgId: string;
  customerOrgName: string;
  dateOrdered: string;
  timestampOrdered?: EpochTimeStamp;
  orderer: string;
  workLocationId: string;
  workLocationName: string;
  workLocationDescriptionForEezy?: string;
  amount: number;
  workStarts: EpochTimeStamp;
  workEnds: EpochTimeStamp;
  orderRowTemplateId: string;
  orderRowTemplateName: string;
  reasonForTemporaryEmployment: string;
  reasonsForEdits?: { editReason: string; editor: string; time: number }[];
  status: OrderStatus;
  cancelReason?: string;
  // First / "main" handler for the order
  orderHandler: OrderHandler | null;
  orderHandlers: OrderHandler[];
  shiftDetails: ShiftDetails2[];
  eezyCompanyId: string;
  taskSpec: {
    workTitleId: string;
    skillIds: string[];
    certificateIds: string[];
    languageSkills: LanguageSkill[];
    trainings: string[];
    description?: string;
    descriptionEn?: string;
    descriptionForTalent?: string;
    descriptionForTalentEn?: string;
    tesId: string;
    tesVariation: VariationSelection[];
  };
  customerCostCenterIds: string[];
  shortWorkTimeDescriptionFi?: string;
  shortWorkTimeDescriptionEn?: string;
  longWorkTimeDescriptionFi?: string;
  longWorkTimeDescriptionEn?: string;
  descriptionToEezyAndCustomer?: string;
  descriptionToEezyAndCustomerAndTalent?: string;
  version: number;
  // Note: this field will be deprecated - replaced by shiftDetails and taskSpec
  orderRows: OrderRowEdit[];
  workLocationAddress?: {
    street: string;
    zip: string;
    city: string;
    country: string;
  };
  teamNotificationSettings?: TeamNotificationSettings;
  targetedShiftTargetingRules: OrderEditTargetingRule[];
}

export type UpdateOrderRowBundle = Omit<
  OrderEdit,
  "status" | "reasonsForEdits"
> & {
  automatchingStatus: AutomatchingStatus;
  automatchingStartDelayMinutes: number | null;
  reasonForEdit: string;
  editor: string;
};

export interface UpdateLongOrderRowBundle {
  bundleId: string;
  version: number;
  type: OrderType;
  reasonForTemporaryEmployment?: string;
  startDate: string;
  endDate?: string;
  customerCostCenterIds: string[];
  requiredTalentAmount: number;
  tesVariation: VariationSelection[];
  shortWorkTimeDescriptionFi: string;
  shortWorkTimeDescriptionEn: string;
  longWorkTimeDescriptionFi: string;
  longWorkTimeDescriptionEn: string;
  talentRequirements: {
    certificates: string[];
    languages: LanguageSkill[];
    skills: string[];
    trainings: string[];
  };
  workDescriptionFi: string;
  workDescriptionEn: string;
  arrivalInstructionsFi: string;
  arrivalInstructionsEn: string;
  descriptionToEezyAndCustomer: string;
  reasonForChange: string;
  targetedShiftTargetingRules: TargetingRule[];
  //editor: string;
}

export type NewOrder = Omit<
  Order,
  "id" | "orderTime" | "orderTitle" | "workStarts" | "customerOrgName"
>;

export interface TaskSpec {
  amount?: number;
  workTitleId: string;
  skillIds: Array<string>;
  certificateIds: Array<string>;
  trainings: Array<string>;
  languageSkills: { languageCode: LanguageSkillCode; skillLevel: string }[];
  tesId: string;
  description: string;
  descriptionEn: string;
  descriptionForTalent: string;
  descriptionForTalentEn: string;
  tesVariation: VariationSelection[];
  supplements: SupplementsType;
  supplementPriceOverrides: SupplementPriceOverridesType;
  periodStartDate: Date | string | null;
}

export interface ShiftDetails {
  shiftId?: string;
  incrementId: number;
  amount: number;
  startDate: number | null; //TODO: end times for short order not done
  endDate: number | null; //done so the compiler is happy, needs review
  startTime: number | null;
  endTime: number | null;
  additionalInfo?: string;
}

export interface FixedTermDetail {
  termDetailsId: number;
  amount: number;
  startDate: Date | null;
  endDate: Date | null;
  startTime: number | null; //todo: naming is confusing, seems to refer to startDate but does not
  endTime: number | null; //todo: same as with startTime
  shiftWorkType?: string;
  rotation?: string;
  additionalInfo?: string; //TODO: description?
}

export interface OrderRow {
  id?: string;
  version?: number;
  incrementId: number;
  eezyCCId: string;
  custCCIds: Array<string>;
  taskSpec: TaskSpec;
  shiftDetails: ShiftDetails[];
}

export interface OrderRowLongOrder extends OrderRow {
  fixedTermDetail: FixedTermDetail;
}

export interface OrderRowShortOrder extends OrderRow {
  shiftDetails: Array<ShiftDetails>;
}

export interface SkillsFilter {
  term?: string;
  workTitleIds: string;
  language: string;
}

export interface CertificatesFilter {
  term?: string;
  language: string;
}

export interface Skill {
  id: string;
  fieldOfWorkId: string;
}

export interface Certificate {
  id: string;
  name: string;
}

export type OrderRowNew =
  | OrderRowLongOrder
  | OrderRowShortOrder
  | (OrderRowLongOrder & { shiftDetails: [] })
  | (OrderRowShortOrder & { fixedTermDetail: null });

export function isLongOrder(
  orderRow: OrderRowLongOrder | OrderRowShortOrder,
): orderRow is OrderRowLongOrder {
  return (orderRow as OrderRowLongOrder).fixedTermDetail !== undefined;
}

//With the typescript 2.8 feature conditional types
//we should be able to create the types using Extract and
//other helper functions
//see:
//https://stackoverflow.com/questions/50125893/typescript-derive-map-from-discriminated-union/50125960#50125960
//https://stackoverflow.com/questions/46312206/narrowing-a-return-type-from-a-generic-discriminated-union-in-typescript

// FIXME: Is this dead code?
export interface OrderBasicInfo {
  title: string;
  customerOrganizationId: string;
  contractId: string;
  workLocationId: string;
  orderType: string;
}

export interface WorkTitle {
  name: string;
  fieldOfWorkId?: string; // FIXME: Adding this as required breaks multiple forms. Note: The value is always returned by back-end
  id: string;
}

export interface Event<T> {
  id: string;
  name: string;
  version: number;
  payload: T;
}

export interface OrderFilters {
  id?: string;
  from?: number;
  to?: number;
}

export interface OrderRowFilters {
  search: string;
  filters: {
    queue?: string;
    dateFrom?: string;
    dateTo?: string;
    handler?: string;
    fulfilled?: string;
    workLocation?: string;
    type?: string;
    automatching?: string;
    urgency?: string;
  };
  cursor?: string | null;
  pageSize?: string;
}

export interface CreateAssignment {
  talentId: string;
  orderRowId: string;
  orderRowVersion: number;
  shiftId?: string;
}

export type ContractId = string;

export interface OffersStatusBreakdown {
  workOffersTotal: number;
  workOffersCancelled: number;
  workOffersAccepted: number;
  workOffersDeclined: number;
  workOffersOffered: number;
  workOffersNoResponse: number;
}

export interface OffersStatus {
  manualOffers: OffersStatusBreakdown;
  automatchOffers: OffersStatusBreakdown;
}

export interface OrderWorkOffers {
  id: string;
  automatched: TalentWorkOfferStatus[];
  manuallyOffered: TalentWorkOfferStatus[];
}

export interface TalentWorkOfferStatus {
  manuallyOffered: boolean;
  id: string;
  firstName: string;
  lastName: string;
  workOfferStatus: string;
}

export type ManualOfferTalent = Omit<
  TalentWorkOfferStatus,
  "manuallyOffered" | "workOfferStatus"
> & {
  status: string;
};

export interface TalentWorkOfferFilters {
  search: string;
  bundleId: string;
}

export interface WorkLocationSuggestedTalentFilters {
  bundleId: string;
  workLocationId: string;
}

// TODO: use EventPayload type
export interface OrderRowEvent {
  id: string;
  name: string;
  version: number;
  payload: any;
}

export interface SendManualWorkOffer {
  talentId: string;
  firstName: string;
  lastName: string;
  bundleId: string;
}

export type CancelWorkOffer = {
  talentIds: string[];
  bundleId: string;
};

export interface SendDissassembleBundle {
  bundleId: string;
  orderRowIds: { id: string; version: number }[];
}

export interface OrderQueue {
  id: string;
  name: string;
}

export interface SendWorkOffer {
  orderRowId: string;
  talentId: string;
  workLocationId: string;
  salaryOverride?: number;
  expiryDate: Date;
}

export interface ManualOfferSearchRequirements {
  search?: string;
  workLocationId: string;
  workTitleId: string;
  tesId: string;
  languages?: string;
  certificates?: string;
  trainings?: string;
  salaryLimit?: string;
  schedule: string;
  fromWorkLocationTalentPool?: boolean;
}

export interface ManualOfferSearchForBundle {
  bundleId: string;
  search: string;
}

export interface UpdateOrderHandlers {
  bundleId: string;
  handlerIds: string[];
  handlerNames: string[];
}

// TODO cleanup
export interface LongOrderTalent {
  id: string;
  talentId: string;
  firstName: string;
  lastName: string;
  workAgreementId: string;
}

export interface WorkAgreementTargetedShiftSalarySpec {
  keys: {
    custCompanyId: string;
    custOrgs: string[];
    workLocs: string[];
    workTitles: string[];
  };
  salary: DateBasedSalary;
}

// TODO update when we know more
export interface OfferWorkAgreement {
  bundleId: string;
  talentId: string;
  workTimeType: WorkTimeType;
  startDate: string;
  endDate?: string;
  probationEndDate?: string | null;
  agreedHours: AgreedHours;
  tesVariation: VariationSelection[];
  supplements: any;
  talentAgreesExtraWork: boolean;
  schedulingClass: string;
  salaryType: SalaryType;
  salary: any;
  tesSalaryTableSelection?: TesSalaryTableSelectionProvenance | null;
  annualHolidayAccumulationRule: any;
  isAveragingPeriodInUse: boolean;
  extraInformation?: string;
  messageForExpert?: string;
  invoicingPriceOverride?: string;
  periodStartDate?: string;
  supplementPriceOverrides?: Record<string, string>;
  replaceWorkAgreementId?: string;
  targetedShiftSalarySpecs?: WorkAgreementTargetedShiftSalarySpec[];
  createHrTaskWhenTalentAcceptsAgreement?: boolean;
}

/**
 * AddAssignment goes to working api, but here in UI it is all about orders.
 * This actually creates a copy of an agreement, currently originalAssignmentId is required.
 * The intention is to allow creation of overlapping assignment when the certain conditions are met.
 */
export interface AddAssignment {
  talentId: string;
  orderRowId: string;
  orderQueueId: string;
  bundleId: string;
  employmentId: string;
  originalAssignmentId: string; // Do we need, or do we trust and require all params;
  customerOrgId: string;
  customerCostCenterIds: string[];
  startDate: string;
  endDate: string;
  contractId: ContractId;
  workTitleId: string;
  workLocationId: string;
  supplements: any;
  supplementPriceOverrides: Record<string, string>;
}

export interface UpdateWorkAgreement
  extends Omit<OfferWorkAgreement, "bundleId" | "talentId"> {
  workAgreementId: string;
  version: number;
}

export interface LongOrderTalents {
  picked: LongOrderTalent[];
  fromJobAdvertisement: LongOrderTalent[];
  recommended: LongOrderTalent[];
}

export type WorkAgreementInfo = {
  eezyCompany: {
    id: string;
    name: string;
  };
  workLocation: {
    id: string;
    name: string;
    address: {
      street: string;
      zip: string;
      city: string;
      country: string;
    };
  };
  tes: {
    id: string;
    supplements: any;
    variations: VariationSelection[];
    salaryComponents: any;
    supplementPriceOverrides?: { [key: string]: SupplementPriceOverride };
  };
  probationMaxDurationInDays: number;
  workTitleId: string;
  reasonForTemporaryEmployment?: string;
  schedulingClassId: string;
  monthlySchedulingClassId: string;
  workStarts: EpochTimeStamp;
  workEnds?: EpochTimeStamp;
  periodStartDate?: string;
  targetedShiftTargetingRules?: TargetingKeys[];
};

export interface WorkAgreement {
  id: string;
  talentId: string;
  bundleId: string;
  version: number;
  status: string;
  workLocationName: string;
  workLocationAddress: {
    street: string;
    zip: string;
    city: string;
    country: string;
  };
  contact: {
    firstName: string;
    lastName: string;
    title: string;
    emailAddress: string;
    companyName: string;
  };
  workAgreement: {
    bundleId: string;
    talentId: string;
    orderQueueId: string;
    orderRowId: string;
    orderRowVersion: number;
    orderAttributes: {
      isExpress: boolean;
      isSelfService: boolean;
      isEarlyBird: boolean;
    };
    eezyCompanyId: string;
    contractId: string;
    customerCostCenterIds: string[];
    workTitleId: string;
    workLocationId: string;
    tesId: string;
    tesVariation: VariationSelection[];
    supplements: any;
    tempEmploymentReasonCode?: string;
    startDate: string;
    endDate?: string;
    probationEndDate: string;
    workTimeType: WorkTimeType;
    agreedHours: AgreedHours;
    talentAgreesExtraWork: boolean;
    paymentPeriodClassId: string;
    salaryType: SalaryType;
    salary: any;
    tesSalaryTableSelection?: TesSalaryTableSelectionProvenance | null;
    periodStartDate: string;
    extraInformation: string;
    messageForExpert: string;
    invoicingPriceOverride: string;
    annualHolidayAccumulationRule: string;
    supplementPriceOverrides: { [key: string]: SupplementPriceOverride };
    targetedShiftSalarySpecs?: WorkAgreementTargetedShiftSalarySpec[];
    createHrTaskWhenTalentAcceptsAgreement?: boolean;
  };
  targetedShiftTargetingRules?: TargetingKeys[];
  targetedShiftSalarySpecs?: TargetedShiftSalaryRow[];
  createHrTaskWhenTalentAcceptsAgreement?: boolean;
  workAgreementDocumentId: string | null;
}

export interface WorkLocationTalentPoolByIdFilters {
  workLocationId: string;
  includeNotActiveTalents?: boolean;
}

export type WorkLocationPoolTalent = {
  id: string;
  firstName: string;
  lastName: string;
};

export interface WorkLocationTalentPoolById {
  workLocationId: string;
  talents: WorkLocationPoolTalent[];
}

export type OrderRowBundleId = string;

export interface OrderBundleComment {
  id: string;
  version: number;
  bundleId: string;
  comment: string;
  dateTime: number;
  authorId: string;
  authorName: string;
}

export interface AddOrderBundleComment {
  bundleId: string;
  comment: string;
}

export interface UpdateOrderBundleComment {
  id: string;
  version: number;
  bundleId: string;
  comment: string;
}

export interface RemoveOrderBundleComment {
  id: string;
  version: number;
}

export interface OrderBundleCommentAdded {
  bundleId: string;
  comment: string;
}

export interface OrderBundleCommentUpdated {
  comment: string;
}

export interface CreateWorkAgreementDocument {
  talentId: string;
  bundleId: string;
}

export const WorkAgreementDocumentTypeOptions = {
  MAIN: "MainDocument",
  ATTACHMENT: "Attachment",
} as const;

export type WorkAgreementDocumentType =
  (typeof WorkAgreementDocumentTypeOptions)[keyof typeof WorkAgreementDocumentTypeOptions];

export interface GetWorkAgreementDocumentUploadUrl {
  workAgreementDocumentId: string;
  type: WorkAgreementDocumentType;
  size: number;
  filename: string;
}

export interface WorkAgreementDocumentUploadUrlResponse {
  url: string;
  filename: string;
}

export const ApprovalReadinessOptions = {
  MAIN_DOCUMENT_MISSING: "MAIN_DOCUMENT_MISSING",
  SIGNING_DATE_MISSING: "SIGNING_DATE_MISSING",
  READY: "READY",
} as const;

export type ApprovalReadiness =
  (typeof ApprovalReadinessOptions)[keyof typeof ApprovalReadinessOptions];

export const WorkAgreementDocumentSourceOptions = {
  EXPERT_UPLOAD: "EXPERT_UPLOAD",
  SYSTEM_GENERATE: "SYSTEM_GENERATE",
} as const;

export type WorkAgreementDocumentSource =
  (typeof WorkAgreementDocumentSourceOptions)[keyof typeof WorkAgreementDocumentSourceOptions];

export interface WorkAgreementDocumentInfo {
  workAgreementDocumentId: string;
  workAgreementDocument?: string;
  version: number;
  talentId: string;
  attachments: string[];
  approvalReadiness: ApprovalReadiness;
  signedAt?: EpochTimeStamp;
  source: WorkAgreementDocumentSource;
}

export interface SetWorkAgreementDocumentSigningDate {
  workAgreementId: string;
  signingDate: string;
  version: number;
}

export interface AcceptWorkAgreement {
  workAgreementDocumentId: string;
  workAgreement: OfferWorkAgreement;
}

export interface WorkAgreementDocumentDownloadUrls {
  mainDocument: { url: string };
  attachments?: { url: string }[];
}

export const listOrders = (
  OrderFilters: OrderFilters,
  { requestId }: Options = {},
) => {
  return api.get<Order[]>("/projections/qry/orders", {
    //TODO: wrong query this does not update all the values
    requestId,
    auth: true,
  });
};

export const listOrderRows = (
  filters: OrderRowFilters,
  { requestId }: Options = {},
) => {
  const filterObject = filters.filters;
  let params: Record<string, string> = {};
  if (filters.search.toString()) params["search"] = filters.search.toString();
  if (filters.cursor) params["cursor"] = filters.cursor;
  for (const [key, value] of Object.entries(filterObject)) {
    if (value.toString()) {
      params[key] = value.toString();
    }
  }
  return api.get<Paginated<OrderRowDisplay[]>>("/projections/qry/orderRows3", {
    requestId,
    auth: true,
    queryParams: {
      ...params,
      pageSize: filters?.pageSize || "50",
    },
  });
};

export const listOrderRowsByBundleId = (
  OrderFilters: OrderFilters,
  { requestId }: Options = {},
) => {
  const { id } = OrderFilters;
  return api.get<OrderEdit>(
    "/projections/qry/orderRowsByBundleId?bundleId=" + id,
    {
      requestId,
      auth: true,
    },
  );
};

//REVIEW: Should there be different API for WorkTitles
export interface WorkTitleFilters {
  language: string;
}

export interface SkillFilters {
  language: string;
  workTitleIds?: string;
  term?: string;
}

export interface CancelShiftCmd {
  assignmentId: string;
  paySalary: boolean;
  reason: string; //Should this be enum, only two reasons?
}

export interface ShiftCancelledCmd {
  bundleId: string;
  talentId: string;
  orderRowIds: string[];
  replaceTalent?: boolean;
  setAutomatchingStatus?: AutomatchingStatus;
}

export interface CancelOrderRowBundle {
  bundleId: string;
  reasonForCancellation: string; //Should this be enum, only two reasons?
}

export const listWorkTitles = (
  filters: WorkTitleFilters,
  { requestId }: Options = {},
): Promise<WorkTitle[]> => {
  return api
    .get<WorkTitle[]>("/projections/qry/workTitlesByLanguage", {
      requestId,
      auth: true,
      queryParams: { ...filters },
    })
    .then((res) => res.data);
};

export const postCancelWorkOffers = (
  params: CancelWorkOffer,
  { requestId }: Options,
) => {
  return api.post<EventPayload<{}>[]>("/order/cmd/cancelWorkOffers", {
    requestId,
    auth: true,
    body: {
      ...params,
    },
  });
};
// TODO: To projections api
export const listSkills = (
  filters: SkillFilters,
  { requestId }: Options = {},
): Promise<Skill[]> => {
  return api
    .get<Skill[]>("/projections/qry/skills", {
      requestId,
      auth: true,
      queryParams: { ...filters },
    })
    .then((res) => res.data);
};

export const fetchTESVariations = (tesId: string, date: string) =>
  api
    .get<TESVariationResponse>("/working/qry/TESVariations", {
      requestId: nanoid(),
      auth: true,
      queryParams: {
        tesId,
        date,
      },
    })
    .then((res) => res.data);

export const fetchOrderRowTemplates = (contractId: ContractId) => {
  return api
    .get<OrderRowTemplate[]>("/projections/qry/orderRowTemplates", {
      requestId: nanoid(),
      auth: true,
      queryParams: { contractId: contractId },
    })
    .then((res) => {
      return res.data;
    });
};

export const createLongOrder = (data: OrderPreparedPayload) => {
  return api
    .post<Event<Order | OrderRow>[]>("/order/cmd/createLongOrder", {
      requestId: nanoid(),
      auth: true,
      body: {
        ever: 0,
        ...data,
      },
    })
    .then((res) => res.data);
};

// Note: calls working api !
export const cancelShiftApi = (data: CancelShiftCmd) => {
  return api
    .post<OrderRowEvent[]>("/working/cmd/cancelShift", {
      requestId: nanoid(),
      auth: true,
      body: {
        ...data,
      },
    })
    .then((res) => res.data);
};

export const shiftCancelledApi = (
  data: ShiftCancelledCmd,
  options: Options = {},
) => {
  return command<ShiftCancelledCmd, unknown>(
    "/order/cmd/shiftCancelled",
    data,
    options,
  );
};

export const cancelOrderRowBundle = (data: CancelOrderRowBundle) => {
  return api
    .post<Event<Order | OrderRow>[]>("/order/cmd/cancelOrderRowBundle", {
      requestId: nanoid(),
      auth: true,
      body: {
        ...data,
      },
    })
    .then((res) => res.data);
};

export const removeShiftsFromBundle = (params: SendDissassembleBundle) => {
  return api.post<EventPayload<{}>[]>("/order/cmd/disassembleOrderRowBundle", {
    requestId: nanoid(),
    auth: true,
    body: {
      ...params,
    },
  });
};

export const addOrder = (
  data: OrderPreparedPayload,
  { requestId, idempotencyKey }: Options = {},
) => {
  return api
    .post<Event<Order | OrderRow>[]>("/order/cmd/addOrder", {
      requestId,
      auth: true,
      body: {
        ever: 0,
        ...data,
      },
      ...(idempotencyKey && {
        headers: { "x-idempotency-key": idempotencyKey },
      }),
    })
    .then((res) => res.data);
};

export const addLongOrder = (
  data: OrderPreparedPayload,
  { requestId, idempotencyKey }: Options = {},
) => {
  return api
    .post<Event<Order | OrderRow>[]>("/order/cmd/createLongOrder", {
      requestId,
      auth: true,
      body: {
        ever: 0,
        ...data,
      },
      ...(idempotencyKey && {
        headers: { "x-idempotency-key": idempotencyKey },
      }),
    })
    .then((res) => res.data);
};

export const editShortOrderRow = (data: UpdateOrderRowBundle) => {
  return api
    .post<OrderRowEvent[]>("/order/cmd/editOrderRowBundle", {
      requestId: nanoid(),
      auth: true,
      body: {
        ...data,
      },
    })
    .then((res) => res.data);
};

export const editLongOrderRow = (data: UpdateLongOrderRowBundle) => {
  return api
    .post<OrderRowEvent[]>("/order/cmd/updateLongOrderRowBundle", {
      requestId: nanoid(),
      auth: true,
      body: {
        ...data,
      },
    })
    .then((res) => res.data);
};

export const skillDefinitionsAutoCompleteQuery = async (
  filters: SkillsFilter,
) => {
  return api.get<Skill[]>("/projections/qry/skills", {
    requestId: nanoid(),
    auth: true,
    queryParams: {
      ...(filters.term && { term: filters?.term }),
      workTitleIds: filters.workTitleIds,
      language: filters.language,
    },
  });
};

export const certificateDefinitionsAutoCompleteQuery = async (
  filters: CertificatesFilter,
) => {
  return api.get<Certificate[]>("/projections/qry/certificates", {
    requestId: nanoid(),
    auth: true,
    queryParams: {
      ...(filters.term && { term: filters?.term }),
      language: filters.language,
    },
  });
};

export const getBundleOfferCount = (bundleId: string) => {
  return api
    .get<OffersStatus>("/projections/qry/workOffersCountByBundleId", {
      requestId: nanoid(),
      auth: true,
      queryParams: {
        bundleId,
      },
    })
    .then((res) => res.data);
};

export const searchManualOfferTalents = async (
  requirements: ManualOfferSearchRequirements,
) =>
  api.get<ManualOfferTalent[]>("/order/qry/customerManualOfferTalentsSearch", {
    requestId: nanoid(),
    queryParams: {
      ...filterFalsyParameters(requirements),
    },
    auth: true,
  });

export const searchManualOfferTalentsForBundle = async (
  params: ManualOfferSearchForBundle,
) => {
  return api.get<TalentWorkOfferStatus[]>(
    "/order/qry/manualOfferTalentsSearch",
    {
      requestId: nanoid(),
      queryParams: {
        ...params,
      },
      auth: true,
    },
  );
};

export const searchTalentsByWorkLocationId = (
  workLocationId: string,
  bundleId: string,
) => {
  return api.get<TalentWorkOfferStatus[]>(
    "/projections/qry/talentsByWorkLocationId",
    {
      requestId: nanoid(),
      queryParams: {
        workLocationId,
        bundleId,
      },
      auth: true,
    },
  );
};

export const makeManualOffer = (
  params: SendManualWorkOffer,
  { requestId }: Options,
) => {
  return api.post<EventPayload<{}>[]>("/order/cmd/sendWorkOffer", {
    requestId,
    auth: true,
    body: {
      ...params,
    },
  });
};

export const getWorkOffersStateByBundleId = (bundleId: string) => {
  return api.get<OrderWorkOffers>(
    "/projections/qry/workOffersStateByBundleId",
    {
      requestId: nanoid(),
      queryParams: {
        bundleId,
      },
      auth: true,
    },
  );
};

export const updateOrderRowTemplateActiveStatus = (
  params: UpdateActiveStatus,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<{}>[]>("/order/cmd/updateOrderRowTemplateActiveStatus", {
      requestId,
      auth: true,
      body: {
        ...params,
      },
    })
    .then((res) => res.data[0]);

export const createOrderRowTemplate = (
  orderTemplate: CreateOrderRowTemplate,
  { requestId }: Options,
) => {
  return api
    .post<EventPayload<CreateOrderRowTemplate>[]>(
      "/order/cmd/createOrderRowTemplate",
      {
        requestId,
        auth: true,
        body: {
          ...orderTemplate,
        },
      },
    )
    .then((res) => res.data[0]);
};

export const updateOrderRowTemplate = (
  orderTemplate: UpdateOrderRowTemplate,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<OrderRowTemplateUpdated>[]>(
      "/order/cmd/updateOrderRowTemplate",
      {
        requestId,
        auth: true,
        body: {
          ...orderTemplate,
        },
      },
    )
    .then((res) => res.data);

export const getOrderQueues = () => {
  return api.get<OrderQueue[]>("/projections/qry/orderQueues", {
    requestId: nanoid(),
    auth: true,
  });
};

export const sendWorkOfferRequest = async (
  payload: SendWorkOffer,
  { requestId }: Options,
) => {
  const params = {
    requestId,
    auth: true,
    body: payload,
  };

  return await api.post("/working/cmd/sendWorkOffer", params);
};

export const updateOrderHandlers = async (payload: UpdateOrderHandlers) => {
  return await api.post<
    EventPayload<{ bundleId: string; id: string; name: string }>[]
  >("/order/cmd/setOrderHandlers", {
    requestId: nanoid(),
    auth: true,
    body: payload,
  });
};

export const getTalentsForLongOrder = async (bundleId: string) => {
  return api.get<LongOrderTalents>("/order/qry/talentsForLongOrder", {
    requestId: nanoid(),
    queryParams: {
      bundleId,
    },
    auth: true,
  });
};

export const postOfferWorkAgreement = async (payload: OfferWorkAgreement) => {
  return await api.post<EventPayload<OfferWorkAgreement>[]>(
    "/order/cmd/offerWorkAgreement",
    {
      requestId: nanoid(),
      auth: true,
      body: payload,
    },
  );
};

export const postAddAssignment = async (payload: AddAssignment) => {
  return await api.post<EventPayload<AddAssignment>[]>(
    "/working/cmd/addAssignment",
    {
      requestId: nanoid(),
      auth: true,
      body: payload,
    },
  );
};

export const postUpdateWorkAgreement = async (payload: UpdateWorkAgreement) => {
  return await api.post<EventPayload<UpdateWorkAgreement>[]>(
    "/order/cmd/updateWorkAgreement",
    {
      requestId: nanoid(),
      auth: true,
      body: payload,
    },
  );
};

export const getWorkAgreementInfoFromOrderRowBundleInfo = async (
  bundleId: OrderRowBundleId,
) => {
  return await api.get<WorkAgreementInfo>(
    "/projections/qry/workAgreementInfoFromOrderRowBundleInfo",
    {
      requestId: nanoid(),
      auth: true,
      queryParams: {
        bundleId,
      },
    },
  );
};

export const getWorkAgreement = async (id: string) => {
  return await api.get<WorkAgreement>("/projections/qry/workAgreement", {
    requestId: nanoid(),
    auth: true,
    queryParams: {
      id,
    },
  });
};

export const postCancelWorkAgreementOffer = async (
  workAgreementId: string,
  version: number,
) => {
  return await api.post<OrderRowEvent[]>("/order/cmd/cancelWorkAgreement", {
    requestId: nanoid(),
    auth: true,
    body: {
      workAgreementId,
      version,
    },
  });
};

export const workLocationTalentPoolById = async (
  params: WorkLocationTalentPoolByIdFilters, // conversationI
  options: Options = {},
): Promise<WorkLocationTalentPoolById> =>
  query<WorkLocationTalentPoolByIdFilters, WorkLocationTalentPoolById>(
    "/projections/qry/workLocationTalentPoolById",
    params,
    options,
  );

export const getOrderFulfilled = async (
  params: { bundleId: string },
  options: Options = {},
): Promise<FulfillmentStatusResponse> =>
  query<{ bundleId: string }, FulfillmentStatusResponse>(
    "/order/qry/workOffers",
    params,
    options,
  );

export const getCanCancelEmployment = async (
  params: { bundleId: string; talentId: string },
  options: Options = {},
): Promise<boolean> =>
  query<{ bundleId: string; talentId: string }, boolean>(
    "/working/qry/canCancelEmployment",
    params,
    options,
  );

export const getOrderBundleComments = (
  params: { bundleId: string },
  options: Options = {},
): Promise<OrderBundleComment[]> =>
  query<{ bundleId: string }, OrderBundleComment[]>(
    "/projections/qry/orderBundleComments",
    params,
    options,
  );

export const postAddOrderBundleComment = (
  body: AddOrderBundleComment,
  options: Options = {},
): Promise<EventPayload<OrderBundleCommentAdded>[]> =>
  command<AddOrderBundleComment, OrderBundleCommentAdded>(
    "/order/cmd/addOrderRowBundleComment",
    body,
    options,
  );

export const postUpdateOrderBundleComment = (
  body: UpdateOrderBundleComment,
  options: Options = {},
): Promise<EventPayload<OrderBundleCommentUpdated>[]> =>
  command<UpdateOrderBundleComment, OrderBundleCommentUpdated>(
    "/order/cmd/updateOrderRowBundleComment",
    body,
    options,
  );

export const postRemoveOrderBundleComment = (
  body: RemoveOrderBundleComment,
  options: Options = {},
): Promise<EventPayload<{}>[]> =>
  command<RemoveOrderBundleComment, {}>(
    "/order/cmd/removeOrderRowBundleComment",
    body,
    options,
  );

export const postCreateWorkAgreementDocument = async (
  body: CreateWorkAgreementDocument,
  options: Options = {},
) =>
  command<CreateWorkAgreementDocument, {}>(
    "/working/cmd/createWorkAgreementDocument",
    body,
    options,
  );

export const doGetWorkAgreementDocumentUploadUrl = async (
  body: GetWorkAgreementDocumentUploadUrl,
  options: Options = {},
) =>
  query<
    GetWorkAgreementDocumentUploadUrl,
    WorkAgreementDocumentUploadUrlResponse
  >("/working/qry/getWorkAgreementDocumentUploadUrl", body, options);

export const doUploadWorkAgreementDocument = (
  url: string,
  file: File,
): Promise<void> =>
  api
    .upload(url, {
      headers: {
        "Content-Type": file.type,
        "Content-Length": String(file.size),
      },
      body: file,
    })
    .then(() => undefined);

export const doGetWorkAgreementDocumentInfo = async (
  body: { workAgreementDocumentId: string },
  options: Options = {},
) =>
  query<{ workAgreementDocumentId: string }, WorkAgreementDocumentInfo>(
    "/working/qry/getWorkAgreementDocumentInfo",
    body,
    options,
  );

export const doSetWorkAgreementDocumentSigningDate = async (
  body: SetWorkAgreementDocumentSigningDate,
  options: Options = {},
) =>
  command<SetWorkAgreementDocumentSigningDate, {}>(
    "/working/cmd/setWorkAgreementDocumentSigningDate",
    body,
    options,
  );

export const doAcceptWorkAgreement = async (body: AcceptWorkAgreement) => {
  return await command<AcceptWorkAgreement, {}>(
    "/order/cmd/setWorkAgreementAccepted",
    body,
    { requestId: nanoid() },
  );
};

export const doGetWorkAgreementDocumentDownloadUrls = async (body: {
  workAgreementDocumentId: string;
}) => {
  return await query<
    { workAgreementDocumentId: string },
    WorkAgreementDocumentDownloadUrls
  >("/working/qry/getWorkAgreementDocumentDownloadUrls", body, {
    requestId: nanoid(),
  });
};
